import { useMemo } from "react"
import { gql } from "@apollo/client"
import { useTranslation } from "react-i18next"
import type { CountryIntelligenceRulesAllowances } from "@oysterhr/schema"
import { AllowancesRulesFragment } from "../../hooks/useKBIntegration/fragments"
import type { AdvisorKnowledgeBase, IntelligenceKeys } from "../../types"
import { filterStatutory, paidWhenFrequency, getValueText } from "./_helpers"
import { mapParamToValue } from "../../utils/helpers"

export type Module = AdvisorKnowledgeBase<{
  isoAlpha2Code: string
  partOfGrossSalary: CountryIntelligenceRulesAllowances[]
  notPartOfGrossSalary: CountryIntelligenceRulesAllowances[]
}>

// The type below comes directly from the GraphQL schema above
type KBBonusTranslationComponentProps = React.ComponentProps<Module["TranslationComponent"]>

export const queryFragment: Module["queryFragment"] = gql`
  fragment kbBonusesAndAllowances on CountryIntelligence {
    isoAlpha2Code
    partOfGrossSalary: allowancesRules(partOfGrossSalary: true) {
      ...AllowancesRulesFragment
    }
    notPartOfGrossSalary: allowancesRules(partOfGrossSalary: false) {
      ...AllowancesRulesFragment
    }
  }

  ${AllowancesRulesFragment}
`

export const getTranslationComponent =
  (path: IntelligenceKeys): Module["TranslationComponent"] =>
  ({ data }: KBBonusTranslationComponentProps) => {
    const { t } = useTranslation("knowledge_base/HiringAdvisor")

    const { partOfGrossSalary, notPartOfGrossSalary } = useMemo(
      () => ({
        partOfGrossSalary: filterStatutory(data?.partOfGrossSalary),
        notPartOfGrossSalary: filterStatutory(data?.notPartOfGrossSalary),
      }),
      [data],
    )

    const renderItems = (items: CountryIntelligenceRulesAllowances[]) => {
      return items.map(({ type, approximation, value, paidWhen }, index: number) => {
        const frequency = paidWhenFrequency(t, paidWhen)
        return (
          <li key={index} className="pl-4">
            {t("allowancesRules.content", {
              type: t(`enums.allowanceType.${type}`),
              ...frequency,
              value: getValueText({
                value: value as string[] | undefined,
                approximate: approximation,
                t,
              }),
            })}
          </li>
        )
      })
    }
    const supportingText = mapParamToValue(path, {
      salaryAmount: base => t(`${base}.salaryAmount`, { ns: "knowledge_base/Default" }),
      annualBonusType: base => t(`${base}.annualBonusType`, { ns: "knowledge_base/Default" }),
      benefits: base => t(`${base}.benefits`, { ns: "knowledge_base/Default" }),
      bonus: base => t(`${base}.bonus`, { ns: "knowledge_base/Default" }),
      bonusUneditable: base => t(`${base}.bonusUneditable`, { ns: "knowledge_base/Default" }),
      allowance: base => t(`${base}.allowance`, { ns: "knowledge_base/Default" }),
      allowanceUneditable: base =>
        t(`${base}.allowanceUneditable`, { ns: "knowledge_base/Default" }),
    })

    return (
      <div>
        {supportingText && (
          <>
            <p>{supportingText}</p>
            {(partOfGrossSalary?.length || notPartOfGrossSalary?.length) > 0 && <br />}
          </>
        )}
        <div>
          {partOfGrossSalary?.length > 0 && (
            <div>
              {t("allowancesRules.partOfGross")}
              <ul className="list-inside list-disc py-2">{renderItems(partOfGrossSalary)}</ul>
            </div>
          )}
          {notPartOfGrossSalary?.length > 0 && (
            <div>
              {t("allowancesRules.notPartOfGross")}
              <ul className="list-inside list-disc py-2">{renderItems(notPartOfGrossSalary)}</ul>
            </div>
          )}
        </div>
      </div>
    )
  }

export const checkDataPresence: Module["checkDataPresence"] = data => {
  return !!(data.partOfGrossSalary?.length || data.notPartOfGrossSalary?.length)
}
